/**
 * DownloadButton.tsx - "Download" control for the chat header.
 *
 * Shows only when an existing chat is open (postId set), mirroring ShareButton.
 * Exports the whole conversation — questions, answers, and each answer's sources,
 * with inline citations preserved as working links.
 */

import { useState } from 'react'
import {
    Box,
    Button,
    IconButton,
    Text,
    HStack,
    VStack,
    Spinner,
    TooltipContent,
    TooltipPositioner,
    TooltipRoot,
    TooltipTrigger,
    Portal,
    DialogRoot,
    DialogBackdrop,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogTitle,
    DialogCloseTrigger,
} from '@chakra-ui/react'
import { LuDownload, LuFileText, LuFileCode, LuFileAudio } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { useChatContext, useTTSConfig } from '@/contexts/ChatContext'
import { useChatExport, ChatExportFormat, ChatTTSConfig } from '@/hooks/useChatExport'
import { toaster } from '@/ui/components/ui/toaster'
import { downloadUrl } from '@/utilities/downloadUrl'

// 'audio' is not a document format — it narrates the conversation via TTS and takes a
// different code path, so it is kept out of ChatExportFormat and branched on below.
type DownloadChoice = ChatExportFormat | 'audio'

interface FormatChoice {
    value: DownloadChoice
    labelKey: string
    fallback: string
    icon: JSX.Element
    /** Appended after the translated label, e.g. the " (.mp3)" on Audio. */
    suffix?: string
}

const DOCUMENT_FORMATS: FormatChoice[] = [
    { value: 'docx', labelKey: 'download_word', fallback: 'Word (.docx)', icon: <LuFileText /> },
    { value: 'markdown', labelKey: 'download_markdown', fallback: 'Markdown (.md)', icon: <LuFileCode /> },
]

/**
 * Shorten a filename for display without losing what it is.
 *
 * Chat filenames are built from the conversation's first question, capped at 100
 * characters, so they routinely overflow the dialog. Plain CSS ellipsis would trim the
 * tail and eat the extension, which is the part worth keeping — so the stem is cut and
 * the extension re-appended. The full name still reaches the saved file and the tooltip.
 */
const shortenFilename = (name: string, max = 36): string => {
    if (name.length <= max) {
        return name
    }
    const dot = name.lastIndexOf('.')
    const hasExt = dot > 0 && name.length - dot <= 6
    const stem = hasExt ? name.slice(0, dot) : name
    const ext = hasExt ? name.slice(dot) : ''
    return `${stem.slice(0, Math.max(8, max - ext.length - 1)).trimEnd()}…${ext}`
}

export const DownloadButton: React.FC = () => {
    const { t, i18n } = useTranslation()
    const isRTL = i18n.dir() === 'rtl'
    const { postId } = useChatContext()
    const { loading, result, exportChat, exportAudio, reset } = useChatExport()
    const ttsConfig = useTTSConfig()
    const [open, setOpen] = useState(false)
    const [running, setRunning] = useState<DownloadChoice | null>(null)

    // Only meaningful for an existing chat.
    if (!postId) return null

    // ttsConfig is null until the circle's TTS settings load, and stays null for a
    // circle that has none — offering Audio then only produces a generic error, so the
    // choice appears once it can actually be honoured.
    const canNarrate = Boolean(ttsConfig?.model && ttsConfig?.voice)
    const formats: FormatChoice[] = canNarrate
        ? [
            ...DOCUMENT_FORMATS,
            {
                value: 'audio',
                labelKey: 'download_audio_file',
                fallback: 'Audio',
                // Always .mp3: the server fixes narration to mp3 because that is the
                // only format that survives being split across provider requests and
                // concatenated. The circle's configured format governs per-message
                // audio, not this.
                suffix: ' (.mp3)',
                icon: <LuFileAudio />,
            },
        ]
        : DOCUMENT_FORMATS

    const handleClose = () => {
        setOpen(false)
        setRunning(null)
        reset()
    }

    const showError = () => {
        toaster.create({
            title: t('download_error', 'Something went wrong. Please try again.'),
            type: 'error',
            duration: 4000,
        })
    }

    const handleExport = async (choice: DownloadChoice) => {
        setRunning(choice)
        try {
            if (choice === 'audio') {
                // canNarrate gates the button, so model/voice are present here.
                await exportAudio(postId, ttsConfig as ChatTTSConfig)
                return
            }
            await exportChat(postId, choice)
        } catch {
            showError()
        }
    }

    // The signed URL is short-lived, so saving it can fail long after it was produced.
    const handleSave = async (url: string, filename: string) => {
        try {
            await downloadUrl(url, filename)
        } catch {
            showError()
        }
    }

    return (
        <>
            <TooltipRoot openDelay={500} positioning={{ placement: 'bottom' }}>
                <TooltipTrigger asChild>
                    <IconButton
                        aria-label={t('download_chat', 'Download')}
                        variant="ghost"
                        size="sm"
                        width="36px"
                        height="36px"
                        color="#333333"
                        _hover={{ background: 'var(--cair-accent-subtle)' }}
                        onClick={() => setOpen(true)}
                    >
                        <LuDownload />
                    </IconButton>
                </TooltipTrigger>
                <Portal>
                    <TooltipPositioner>
                        <TooltipContent>{t('download_chat', 'Download')}</TooltipContent>
                    </TooltipPositioner>
                </Portal>
            </TooltipRoot>

            <DialogRoot open={open} onOpenChange={(e) => !e.open && handleClose()}>
                <DialogBackdrop css={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.5)' }} />
                <DialogContent
                    dir={i18n.dir()}
                    css={{
                        position: 'fixed',
                        top: '40%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999,
                        background: 'white',
                        borderRadius: '8px',
                        padding: '16px',
                        width: 'min(92vw, 460px)',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    }}
                >
                    <DialogHeader>
                        <DialogTitle textAlign={isRTL ? 'right' : 'left'} css={{ fontSize: '16px' }}>
                            {t('download_chat', 'Download')}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <Text fontSize="sm" color="gray.600" marginBottom={3} textAlign={isRTL ? 'right' : 'left'}>
                            {t('download_chat_description', 'Download this conversation. Documents keep sources and citations; audio narrates the conversation only.')}
                        </Text>

                        {loading ? (
                            <VStack justify="center" paddingY={4} gap={2}>
                                <Spinner />
                                {running === 'audio' && (
                                    <Text fontSize="xs" color="gray.500" textAlign="center">
                                        {t('download_audio_working', 'Narrating the conversation — this can take a minute.')}
                                    </Text>
                                )}
                            </VStack>
                        ) : result ? (
                            <VStack align="stretch" gap={2}>
                                <Text fontSize="sm" color="gray.600" textAlign={isRTL ? 'right' : 'left'}>
                                    {t('download_ready', 'Your file is ready.')}
                                </Text>
                                <Button
                                    colorScheme="green"
                                    maxWidth="100%"
                                    title={result.filename}
                                    onClick={() => handleSave(result.url, result.filename)}
                                >
                                    <Box flexShrink={0} display="flex"><LuDownload /></Box>
                                    {/* dir="ltr": a filename reads left-to-right even when its
                                        stem is Arabic, so the extension stays at the end rather
                                        than being flung across by bidi reordering. */}
                                    <Box
                                        as="span"
                                        dir="ltr"
                                        minWidth={0}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        whiteSpace="nowrap"
                                    >
                                        {shortenFilename(result.filename)}
                                    </Box>
                                </Button>
                            </VStack>
                        ) : (
                            <VStack align="stretch" gap={2}>
                                {formats.map((f) => (
                                    <Button
                                        key={f.value}
                                        variant="outline"
                                        justifyContent={isRTL ? 'flex-end' : 'flex-start'}
                                        flexDirection={isRTL ? 'row-reverse' : 'row'}
                                        onClick={() => handleExport(f.value)}
                                    >
                                        {f.icon}
                                        {t(f.labelKey, f.fallback)}{f.suffix || ''}
                                    </Button>
                                ))}
                            </VStack>
                        )}
                    </DialogBody>
                    <DialogFooter css={{ justifyContent: 'flex-end', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                        <Button variant="outline" onClick={handleClose}>{t('close', 'Close')}</Button>
                    </DialogFooter>
                    <DialogCloseTrigger />
                </DialogContent>
            </DialogRoot>
        </>
    )
}
